import { useState } from 'react';
import { X, Printer, Copy, Check, BookOpen, QrCode, Loader2, ShieldCheck } from 'lucide-react';
import { useProgramTranscript } from '../hooks/useParticipantTranscripts';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';

interface TranscriptPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId: string;
  userId: string;
}

export function TranscriptPreviewModal({
  isOpen,
  onClose,
  programId,
  userId,
}: TranscriptPreviewModalProps) {
  const { data: transcript, isLoading } = useProgramTranscript(programId, userId);
  const [copiedCode, setCopiedCode] = useState(false);

  if (!isOpen) return null;

  const handleCopyCode = (code: string) => {
    void navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-surface-container-lowest rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-level4 border border-outline-variant/15 overflow-hidden">
        {/* Modal Top Control Bar - Screen Only */}
        <div className="print:hidden flex items-center justify-between px-6 py-4 border-b border-outline-variant/10 bg-surface-container-low/50">
          <div className="flex items-center gap-2 text-primary font-headline font-bold text-sm">
            <ShieldCheck size={18} />
            <span>
              <Bilingual {...copy.reporting.printModalTitle} />
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              disabled={!transcript}
              className="px-4 py-2 rounded-xl bg-primary text-on-primary font-label font-bold text-xs uppercase tracking-wider flex items-center gap-2 shadow-sm hover:bg-primary-container disabled:opacity-50 transition-all"
            >
              <Printer size={15} />
              <span>
                <Bilingual {...copy.reporting.printTranscriptBtn} />
              </span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl text-on-surface-variant hover:bg-surface-container-high transition-colors"
              title="Tutup"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body / Official Transcript Sheet */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-10 space-y-8 print:p-0 print:overflow-visible">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center min-h-[350px] space-y-3">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <p className="text-sm font-body text-on-surface-variant">
                <Bilingual {...copy.reporting.synthesizing} />
              </p>
            </div>
          ) : transcript ? (
            <div className="space-y-8">
              {/* Document Formal Header */}
              <div className="text-center space-y-2 pb-6 border-b border-outline-variant/15">
                <div className="flex justify-center items-center gap-3">
                  <img src="/logo.png" alt="TSL Logo" className="h-14 w-auto object-contain" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-headline font-black text-on-surface tracking-wider uppercase">
                    TARBIYAH SUNNAH LEARNING
                  </h2>
                  <p className="text-xs font-body text-on-surface-variant font-medium">
                    Pendidikan Jarak Jauh (PJJ) Dirasah Islamiyah & Bahasa Arab
                  </p>
                  <div className="mt-3 inline-block px-5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20 text-xs font-headline font-black uppercase tracking-widest">
                    TRANSKRIP HASIL BELAJAR AKADEMIK RESMI
                  </div>
                </div>
              </div>

              {/* Student & Program Credentials */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 p-5 bg-surface-container-low/40 rounded-2xl border border-outline-variant/10 text-xs font-body">
                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    Nama Peserta
                  </span>
                  <span className="text-sm font-headline font-bold text-on-surface block mt-0.5">
                    {transcript.user.name}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    NIP / NIS
                  </span>
                  <span className="text-sm font-mono font-bold text-primary block mt-0.5">
                    {transcript.user.userCode || transcript.user.id.slice(0, 8).toUpperCase()}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    Program Belajar
                  </span>
                  <span className="text-sm font-headline font-bold text-on-surface block mt-0.5 line-clamp-1">
                    {transcript.program.name}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-wider block">
                    Status Kelulusan
                  </span>
                  <span
                    className={`inline-block mt-1 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider ${
                      transcript.summary.isGraduated
                        ? 'bg-success/15 text-success border border-success/30'
                        : 'bg-amber-500/15 text-amber-600 border border-amber-500/30'
                    }`}
                  >
                    {transcript.summary.isGraduated ? 'LULUS (TUNTAS)' : 'SEDANG BERJALAN'}
                  </span>
                </div>
              </div>

              {/* Subject Gradebook Table */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-headline font-bold text-on-surface flex items-center gap-2">
                    <BookOpen size={16} className="text-primary" />
                    <span>Daftar Nilai Capaian Mata Pelajaran</span>
                  </h3>
                  <span className="text-xs font-mono font-bold text-on-surface-variant">
                    Total: {transcript.subjects.length} Mapel
                  </span>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-outline-variant/15 shadow-sm">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-surface-container-low text-on-surface-variant border-b border-outline-variant/15 text-[10px] font-label font-black uppercase tracking-wider">
                        <th className="px-4 py-3 text-center w-10">No</th>
                        <th className="px-4 py-3">Mata Pelajaran</th>
                        <th className="px-4 py-3 text-center">Tahap</th>
                        <th className="px-4 py-3 text-center">Pertemuan</th>
                        <th className="px-4 py-3 text-center">Nilai</th>
                        <th className="px-4 py-3 text-center">Huruf</th>
                        <th className="px-4 py-3 text-center">Predikat</th>
                        <th className="px-4 py-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/10 text-xs font-body">
                      {transcript.subjects.map((sbj, idx) => (
                        <tr
                          key={sbj.id}
                          className="hover:bg-surface-container-low/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-center font-mono font-bold text-on-surface-variant/70">
                            {idx + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-headline font-bold text-on-surface">
                              {sbj.name}
                            </div>
                            {sbj.code && (
                              <span className="text-[10px] font-mono text-on-surface-variant opacity-70">
                                {sbj.code}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-on-surface-variant">
                            {sbj.stageCode ? `Tahap ${sbj.stageCode}` : sbj.stageName}
                          </td>
                          <td className="px-4 py-3 text-center font-mono">
                            {sbj.completedSessions}/{sbj.totalSessions}
                          </td>
                          <td className="px-4 py-3 text-center font-mono font-bold text-on-surface">
                            {sbj.evaluationScore != null
                              ? sbj.evaluationScore.toFixed(1)
                              : `${sbj.attendancePercentage}%`}
                          </td>
                          <td className="px-4 py-3 text-center font-headline font-bold text-primary">
                            {sbj.letterGrade}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="font-arabic font-bold text-primary">
                              {sbj.predicate.arabic}
                            </div>
                            <div className="text-[10px] text-on-surface-variant">
                              {sbj.predicate.indonesian}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span
                              className={`px-2.5 py-1 rounded-full text-[10px] font-headline font-bold tracking-wider uppercase ${
                                sbj.isPassed
                                  ? 'bg-success/15 text-success'
                                  : 'bg-amber-500/15 text-amber-600'
                              }`}
                            >
                              {sbj.isPassed ? 'TUNTAS' : 'BELUM'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cumulative Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/10 text-center space-y-1">
                  <span className="text-[10px] font-headline font-bold text-on-surface-variant uppercase tracking-widest block">
                    Nilai Rata-rata Kumulatif
                  </span>
                  <p className="text-2xl font-headline font-black text-primary m-0">
                    {transcript.summary.cumulativeScore.toFixed(1)}%
                  </p>
                  <span className="text-xs font-mono font-bold text-secondary">
                    Grade: {transcript.summary.letterGrade}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/10 text-center space-y-1">
                  <span className="text-[10px] font-headline font-bold text-on-surface-variant uppercase tracking-widest block">
                    Yudisium & Predikat
                  </span>
                  <p className="text-lg font-arabic font-bold text-primary m-0">
                    {transcript.summary.predicate.arabic}
                  </p>
                  <span className="text-xs font-body font-bold text-on-surface block">
                    {transcript.summary.predicate.indonesian}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-surface-container-low border border-outline-variant/10 text-center space-y-1">
                  <span className="text-[10px] font-headline font-bold text-on-surface-variant uppercase tracking-widest block">
                    Status Akademik
                  </span>
                  <p
                    className={`text-lg font-headline font-black m-0 ${
                      transcript.summary.isGraduated ? 'text-success' : 'text-primary'
                    }`}
                  >
                    {transcript.summary.isGraduated ? 'LULUS (TUNTAS)' : 'SEDANG BERJALAN'}
                  </p>
                  <span className="text-xs font-body text-on-surface-variant">
                    {transcript.summary.passedSubjects} dari {transcript.summary.totalSubjects}{' '}
                    Mapel Tuntas
                  </span>
                </div>
              </div>

              {/* Legal Verification & Signature Card */}
              <div className="pt-6 border-t border-outline-variant/15 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 text-xs font-body">
                <div className="space-y-2 max-w-md">
                  <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
                    <QrCode size={16} />
                    <span>Verifikasi Keabsahan Dokumen</span>
                  </div>
                  <p className="text-[11px] text-on-surface-variant leading-relaxed">
                    Dokumen ini sah dan diterbitkan resmi oleh Tarbiyah Sunnah Learning.
                  </p>
                  <div className="inline-flex items-center gap-2 p-2 rounded-xl bg-surface-container-low border border-outline-variant/15 font-mono text-[11px]">
                    <span className="text-on-surface-variant">
                      {transcript.summary.verificationCode}
                    </span>
                    <button
                      onClick={() => handleCopyCode(transcript.summary.verificationCode)}
                      className="p-1 hover:bg-primary/10 rounded text-primary transition-colors"
                      title="Salin Kode Verifikasi"
                    >
                      {copiedCode ? (
                        <Check size={14} className="text-success" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Signature Block */}
                <div className="text-center space-y-2 sm:self-end">
                  <span className="text-[10px] text-on-surface-variant font-medium block">
                    Bandung,{' '}
                    {new Date(transcript.summary.issuedAt).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                  <div className="h-12 flex items-center justify-center">
                    <div className="w-28 h-10 border border-dashed border-primary/30 rounded-lg flex items-center justify-center text-[10px] font-bold text-primary/40 uppercase tracking-widest">
                      Tanda Tangan
                    </div>
                  </div>
                  <span className="text-xs font-headline font-bold text-on-surface block">
                    Mudir Mahad / Bagian Akademik
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-12 text-center text-on-surface-variant font-body italic">
              Data transkrip tidak ditemukan untuk peserta ini.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
